import React, { useState } from 'react';
import faqData from '../../data/faq.json';

const faqs = faqData.categories;

// Flatten all questions into a single list with category tag
const allItems = faqs.flatMap(cat =>
    cat.items.map(item => ({ ...item, category: cat.category }))
);

export default function FAQSection() {
    const [activeFilter, setActiveFilter] = useState('All');
    const [openIdx, setOpenIdx] = useState(null);
    const [showAll, setShowAll] = useState(false);

    const INITIAL_COUNT = 8;

    const handleFilter = (cat) => {
        setActiveFilter(cat);
        setOpenIdx(null);
        setShowAll(false);
    };

    const visible = activeFilter === 'All'
        ? allItems
        : allItems.filter(i => i.category === activeFilter);

    const displayed = showAll ? visible : visible.slice(0, INITIAL_COUNT);
    const hasMore = visible.length > INITIAL_COUNT && !showAll;

    return (
        <section id="faq" className="bsides-faq" style={{position:'relative',overflow:'hidden'}}>
            <style>{`
                .bsides-faq {
                    background: var(--eventflow-black);
                    padding: 100px 0;
                    position: relative; overflow: hidden;
                }
                .bsides-faq::before {
                    content: '';
                    position: absolute; inset: 0;
                    background: radial-gradient(ellipse at 50% 100%, rgba(var(--eventflow-primary-rgb),0.1) 0%, transparent 55%);
                    pointer-events: none;
                }

                /* ── filter tabs ── */
                .faq-filters {
                    display: flex; gap: 8px;
                    flex-wrap: wrap; justify-content: center;
                    margin-bottom: 48px;
                }
                .faq-filter {
                    font-family: var(--eventflow-font);
                    font-size: 13px; font-weight: 600;
                    padding: 9px 20px; border-radius: 50px;
                    border: 1px solid rgba(255,255,255,0.1);
                    background: transparent;
                    color: rgba(255,255,255,0.45);
                    cursor: pointer; transition: all 0.25s;
                }
                .faq-filter:hover {
                    border-color: rgba(var(--eventflow-base-rgb),0.4);
                    color: #fff;
                }
                .faq-filter--active {
                    background: var(--eventflow-base);
                    border-color: var(--eventflow-base);
                    color: #fff;
                }

                /* ── flat list ── */
                .faq-list {
                    max-width: 800px;
                    margin: 0 auto;
                    display: flex; flex-direction: column; gap: 10px;
                }

                /* ── accordion item ── */
                .faq-item {
                    background: rgba(255,255,255,0.03);
                    border: 1px solid rgba(255,255,255,0.07);
                    border-radius: 12px; overflow: hidden;
                    transition: border-color 0.3s;
                    animation: faqItemIn 0.25s ease both;
                }
                @keyframes faqItemIn {
                    from { opacity: 0; transform: translateY(8px); }
                    to   { opacity: 1; transform: translateY(0); }
                }
                .faq-item--open { border-color: rgba(var(--eventflow-base-rgb),0.35); }

                .faq-item__q {
                    width: 100%; background: none; border: none;
                    padding: 20px 24px;
                    display: flex; align-items: center;
                    justify-content: space-between; gap: 16px;
                    cursor: pointer; text-align: left;
                }
                .faq-item__left {
                    display: flex; align-items: center; gap: 12px; flex: 1;
                }
                .faq-item__cat-dot {
                    width: 6px; height: 6px; border-radius: 50%;
                    background: var(--eventflow-base);
                    flex-shrink: 0;
                    box-shadow: 0 0 6px rgba(var(--eventflow-base-rgb),0.6);
                }
                .faq-item__q-text {
                    font-family: var(--eventflow-font);
                    font-size: 15px; font-weight: 600;
                    color: #fff; line-height: 1.4;
                }
                .faq-item__icon {
                    width: 28px; height: 28px; border-radius: 50%;
                    background: rgba(var(--eventflow-base-rgb),0.1);
                    border: 1px solid rgba(var(--eventflow-base-rgb),0.2);
                    display: flex; align-items: center; justify-content: center;
                    flex-shrink: 0; transition: background 0.3s, transform 0.3s;
                }
                .faq-item--open .faq-item__icon {
                    background: var(--eventflow-base); transform: rotate(45deg);
                }
                .faq-item__icon i { font-size: 11px; color: var(--eventflow-base); }
                .faq-item--open .faq-item__icon i { color: #fff; }

                .faq-item__a {
                    padding: 0 24px 20px 52px;
                    font-family: var(--eventflow-font);
                    font-size: 14px; line-height: 1.75;
                    color: rgba(255,255,255,0.55);
                    border-top: 1px solid rgba(255,255,255,0.05);
                    padding-top: 16px;
                    animation: faqSlideIn 0.2s ease;
                }
                @keyframes faqSlideIn {
                    from { opacity: 0; transform: translateY(-4px); }
                    to   { opacity: 1; transform: translateY(0); }
                }

                /* ── CTA ── */
                .faq-cta {
                    text-align: center; margin-top: 56px;
                    padding: 36px 40px;
                    background: rgba(255,255,255,0.02);
                    border: 1px solid rgba(255,255,255,0.06);
                    border-radius: 16px;
                    max-width: 560px; margin-left: auto; margin-right: auto;
                }
                .faq-cta h4 {
                    font-family: var(--eventflow-font);
                    font-size: 20px; font-weight: 700; color: #fff; margin-bottom: 8px;
                }
                .faq-cta p {
                    font-size: 14px; color: rgba(255,255,255,0.4); margin-bottom: 20px;
                }

                @media (max-width: 576px) {
                    .faq-filters { gap: 6px; }
                    .faq-filter { font-size: 12px; padding: 7px 14px; }
                    .faq-item__q { padding: 16px 18px; }
                    .faq-item__a { padding: 0 18px 16px 18px; padding-top: 12px; }
                    .faq-item__q-text { font-size: 14px; }
                    .faq-cta { padding: 28px 20px; }
                }
            `}</style>

            <div className="container">
                {/* Header */}
                <div className="text-center mb-5 wow fadeInUp" data-wow-delay="100ms">
                    <div className="bsides-section-tag" style={{justifyContent:'center'}}>{faqData.section.eyebrow}</div>
                    <h2 className="bsides-section-title" style={{textAlign:'center'}}>
                        {faqData.section.title} <span>{faqData.section.titleAccent}</span>
                    </h2>
                    <p style={{color:'rgba(255,255,255,0.5)',fontSize:'15px',maxWidth:'480px',margin:'0 auto'}}>
                        {faqData.section.subtitle}
                    </p>
                </div>

                {/* Filter tabs */}
                <div className="faq-filters wow fadeInUp" data-wow-delay="150ms">
                    <button
                        className={`faq-filter${activeFilter === 'All' ? ' faq-filter--active' : ''}`}
                        onClick={() => handleFilter('All')}
                    >
                        All
                    </button>
                    {faqs.map((cat, i) => (
                        <button
                            key={i}
                            className={`faq-filter${activeFilter === cat.category ? ' faq-filter--active' : ''}`}
                            onClick={() => handleFilter(cat.category)}
                        >
                            {cat.category}
                        </button>
                    ))}
                </div>

                {/* Flat question list — one by one */}
                <div className="faq-list wow fadeInUp" data-wow-delay="200ms">
                    {displayed.map((item, idx) => {
                        const isOpen = openIdx === idx;
                        return (
                            <div
                                className={`faq-item${isOpen ? ' faq-item--open' : ''}`}
                                key={`${activeFilter}-${idx}`}
                                style={{ animationDelay: `${idx * 40}ms` }}
                            >
                                <button className="faq-item__q" onClick={() => setOpenIdx(isOpen ? null : idx)}>
                                    <span className="faq-item__left">
                                        <span className="faq-item__cat-dot" />
                                        <span className="faq-item__q-text">{item.q}</span>
                                    </span>
                                    <span className="faq-item__icon"><i className="fa fa-plus" /></span>
                                </button>
                                {isOpen && <div className="faq-item__a">{item.a}</div>}
                            </div>
                        );
                    })}
                </div>

                {/* View More button */}
                {hasMore && (
                    <div className="text-center mt-4 wow fadeInUp" data-wow-delay="250ms">
                        <button
                            className="thm-btn-outline"
                            onClick={() => setShowAll(true)}
                            style={{cursor:'pointer', border:'none'}}
                        >
                            View All {visible.length} Questions <span className="icon-arrow-right" />
                        </button>
                    </div>
                )}

            </div>
        </section>
    );
}
